/**
 * Composable untuk handle intersection observer untuk infinite scroll
 * @param {Object} options - Configuration options
 * @param {Function} options.onIntersect - Callback saat element terlihat
 * @param {Object} options.root - Root element untuk observer (default: null)
 * @param {string} options.rootMargin - Root margin (default: '0px')
 * @param {number} options.threshold - Threshold untuk intersection (default: 0.1)
 * @returns {Object} Object berisi observer state dan methods
 */
const useIntersectionObserver = ({
  onIntersect,
  root = null,
  rootMargin = '0px',
  threshold = 0.1,
} = {}) => {
  let observer = null

  const setup = element => {
    // Create observer jika belum ada
    if (!observer && onIntersect) {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && onIntersect) {
              onIntersect(entry)
            }
          })
        },
        {
          root,
          rootMargin,
          threshold,
        },
      )
    }

    if (!element || !observer) {
      return
    }

    // Disconnect semua observation sebelumnya, lalu observe element baru
    observer.disconnect()
    observer.observe(element)
  }

  const observe = element => {
    if (!element) {
      return
    }

    // Jika observer belum dibuat, buat dulu
    if (!observer && onIntersect) {
      observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && onIntersect) {
              onIntersect(entry)
            }
          })
        },
        {
          root,
          rootMargin,
          threshold,
        },
      )
    }

    if (!observer) {
      return
    }

    // Disconnect semua observation sebelumnya, lalu observe element baru
    observer.disconnect()
    observer.observe(element)
  }

  const disconnect = () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  const cleanup = () => {
    disconnect()
  }

  return {
    setup,
    observe,
    disconnect,
    cleanup,
  }
}

export default useIntersectionObserver
